import { useEffect, useRef } from 'react';

/**
 * Helps track the props changes made in a React functional component.
 *
 * Prints the name of the properties/states variables causing a render (or re-render).
 * For debugging purposes only.
 *
 * @usage You can simply track the props of the components like this:
 *  useRenderingTrace('MyComponent', props);
 *
 * @usage You can also track additional state like this:
 *  const [someState] = useState(null);
 *  useRenderingTrace('MyComponent', { ...props, someState });
 *
 * @param componentName Name of the component to display
 * @param propsAndStates
 * @param level Console log level — default is debug
 *
 * @see https://stackoverflow.com/a/66604983
 */

// DO NOT USE THIS IN PRODUCTION! This is for debugging purposes on your local machine only.
export const useRenderingTrace = (
  componentName: string,
  propsAndStates: any,
  level: 'debug' | 'info' | 'log' = 'debug'
) => {
  const previousPropsAndStates = useRef(propsAndStates);

  useEffect(() => {
    if (['development', 'test'].includes(process.env.NODE_ENV)) {
      const changedProps: { [key: string]: { old: any, new: any } } = Object.entries(propsAndStates).reduce(
        (property: any, [key, value]: [string, any]) => {
          if (previousPropsAndStates.current[key] !== value) {
            property[key] = {
              old: previousPropsAndStates.current[key],
              new: value,
            };
          }

          return property;
        },
        {}
      );

      if (Object.keys(changedProps).length > 0) {
        console[level](`[${componentName}] Changed props:`, changedProps);
      }

      previousPropsAndStates.current = propsAndStates;
    }
  });
};
